// components/FeaturedJobs.tsx

import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Job, jobsData } from "../../data/jobsData";

const FeaturedJobs: React.FC = () => {
  // Only show first 3 jobs
  const featuredJobs = jobsData.slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
  };

  const getCategoryStyles = (color: Job["categoryColor"]) => {
    switch (color) {
      case "purple":
        return "bg-[#8B52FF]/10 text-[#8B52FF]";
      case "coral":
        return "bg-[#E05A6A]/10 text-[#E05A6A]";
      case "mixed":
        return "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF]";
      default:
        return "bg-[#8B52FF]/10 text-[#8B52FF]";
    }
  };

  return (
    <section className="relative py-16 bg-gradient-to-b from-[#FAFAFA] to-white overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#8B52FF]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-[#E05A6A]/5 rounded-full blur-[120px]" />

      {/* Subtle pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(#8B52FF 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-sm font-medium text-[#8B52FF] mb-4"
          >
            Opportunities
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Featured{" "}
            <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
              Positions
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover opportunities that match your skills and start contributing
            to AI development
          </p>
        </motion.div>

        {/* Jobs Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {featuredJobs.map((job, index) => (
            <motion.div key={job.id} variants={itemVariants} className="group">
              <div className="h-full p-6 lg:p-8 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#8B52FF]/20 transition-all duration-300 flex flex-col">
                {/* Header - Category & Pay */}
                <div className="flex items-center justify-between mb-5">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${getCategoryStyles(job.categoryColor)}`}
                  >
                    {job.category}
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {job.payRange}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#8B52FF] transition-colors duration-300">
                  {job.title}
                </h3>

                {/* Location & Schedule */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#8B52FF]" />
                    {job.locationType}
                  </span>
                  {/* <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#E05A6A]" />
                    {job.schedule}
                  </span> */}
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                  {job.description}
                </p>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {job.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-3 py-1.5 rounded-lg bg-gray-50 text-xs font-medium text-gray-600 border border-gray-100"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* CTA Button */}
                <Link to={`/careers/${job.id}`} className="mt-auto">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold shadow-lg shadow-[#8B52FF]/20 hover:shadow-xl hover:shadow-[#8B52FF]/30 transition-all duration-300 flex items-center justify-center gap-2 group/btn"
                  >
                    View Details
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Explore All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col items-center">
            <p className="text-gray-600 mb-6">
              Looking for something different? We have{" "}
              <span className="font-semibold text-[#8B52FF]">
                {jobsData.length}+ positions
              </span>{" "}
              available
            </p>
            <Link to="/careers">
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group px-5 py-2 rounded-xl border-2 border-[#8B52FF] text-[#8B52FF] font-semibold text-base hover:bg-[#8B52FF] hover:text-white transition-all duration-300 flex items-center gap-3"
              >
                Explore All Opportunities
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedJobs;
